import { routes } from "router"
import { useCallback, useEffect, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { createTrip, getGuideTrips, cleanTrip, tripsWithoutEmptySelector, getGuideTripsSearch } from "store/trips"
import { metadataSelector, searchTripsSelector, searchTripsLoadingSelector } from "store/selectors"
import { EXPERIENCE_STATES } from "constants/experiences"
import { useTripSearchLogic } from "./useTripSearchLogic"
import SearchCategory from "../pagesV2/pro/Experiences/Home/TripSearchBox"
import { TabContent } from "../pagesV2/pro/Experiences/Home/TabContent"
import styles from "../pagesV2/pro/Experiences/Home/ExperiencesHome.module.css"
import { useDebounce } from "hooks/useDebounce"

export const useTripSearch = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const key = searchParams.get("route")
  const trips = useSelector(tripsWithoutEmptySelector)
  const searchTrips = useSelector(searchTripsSelector)
  const searchLoading = useSelector(searchTripsLoadingSelector)
  const loading = useSelector((state) => state.trips.loading)
  const metadata = useSelector(metadataSelector("trips"))
  const [searchQuery, setSearchQuery] = useState("")
  const [confirmedSearchQuery, setConfirmedSearchQuery] = useState("")
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [activeTab, setActiveTab] = useState("1")
  const [showSuggestions, setShowSuggestions] = useState(true)
  const [showPagination, setShowPagination] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [originalStatusCount, setOriginalStatusCount] = useState(null)
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  // Preserve original status_count before search API overwrites it
  useEffect(() => {
    if (metadata?.status_count && !searchQuery.trim()) {
      // Store status_count when not searching
      setOriginalStatusCount(metadata.status_count)
    }
  }, [metadata?.status_count, searchQuery])

  // Use normal trips for main list unless confirmedSearchQuery is set
  const tripsForMainList = useMemo(() => {
    if (selectedTrip) return [selectedTrip]
    if (confirmedSearchQuery.trim()) return searchTrips
    return trips
  }, [selectedTrip, confirmedSearchQuery, searchTrips, trips])

  const { categorizedSearchResults } = useTripSearchLogic({
    trips: showSuggestions && debouncedSearchQuery.trim() ? searchTrips : trips,
    searchQuery,
    selectedTrip,
    showSuggestions,
    debouncedSearchQuery
  })

  // For main list, use confirmedSearchQuery
  const mainListLogic = useTripSearchLogic({
    trips: tripsForMainList,
    searchQuery: confirmedSearchQuery,
    selectedTrip,
    showSuggestions: false,
    debouncedSearchQuery: confirmedSearchQuery
  })

  const createTripHandler = useCallback(() => {
    dispatch(createTrip()).then(({ data: trip }) => {
      if (trip?.id) navigate(routes.proNewListingDetailsPath({ id: trip.id }))
    })
  }, [dispatch, navigate])

  useEffect(() => {
    const tabKeyMap = {
      draft: "2",
      published: "1",
      deactivated: "3"
    }
    const newTabKey = tabKeyMap[key]
    if (newTabKey) {
      setActiveTab(newTabKey)
    }
  }, [key])

  // Fetching Trips Based on Tab and Pagination
  const tabStatusMap = { 1: "published", 2: "draft", 3: "unpublished" }

  useEffect(() => {
    if (!activeTab) return

    // Skip if user is viewing search results or a selected trip
    if (confirmedSearchQuery.trim() || selectedTrip) return

    // Prefer the explicit key mapping, fallback to tabStatusMap
    let status = tabStatusMap[activeTab]
    if (key === "deactivated") status = "unpublished"
    else if (key === "draft") status = "draft"
    else if (key === "published") status = "published"

    dispatch(
      getGuideTrips(null, {
        q: { status_eq: status },
        page,
        per_page: pageSize,
        sort_by: "updated_at_desc"
      })
    )

    return () => {
      dispatch(cleanTrip())
    }
  }, [dispatch, activeTab, page, pageSize, key, confirmedSearchQuery, selectedTrip])

  // Fetching Trips on Search (suggestions while typing)
  // This useEffect ONLY depends on debouncedSearchQuery to ensure proper debouncing
  useEffect(() => {
    // Skip if user has confirmed a search (handled by the pagination useEffect below)
    if (confirmedSearchQuery.trim()) return

    // Fetch suggestions based on debouncedSearchQuery only
    // This ensures API calls fire correctly even when user cuts/deletes text
    // Minimum 3 characters required to avoid too broad searches
    // Note: selectedTrip is NOT in dependencies to avoid bypassing debounce
    // when selectedTrip changes. We check it directly here instead.
    if (debouncedSearchQuery.trim().length >= 3 && !selectedTrip) {
      dispatch(
        getGuideTripsSearch(null, {
          q: { title_cont: debouncedSearchQuery.trim() },
          page,
          per_page: pageSize,
          sort_by: "updated_at_desc",
          search: true
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, dispatch, page, pageSize, confirmedSearchQuery])

  // Hide suggestions immediately when user clears search (no debounce needed)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setShowSuggestions(false)
    }
  }, [searchQuery])

  // Fetching paginated search results when user has confirmed a search
  useEffect(() => {
    if (confirmedSearchQuery.trim() && !selectedTrip) {
      // Determine status based on activeTab
      const tabStatusMap = { 1: "published", 2: "draft", 3: "unpublished" }
      let status = tabStatusMap[activeTab]
      if (key === "deactivated") status = "unpublished"
      else if (key === "draft") status = "draft"
      else if (key === "published") status = "published"

      dispatch(
        getGuideTripsSearch(null, {
          q: {
            title_cont: confirmedSearchQuery.trim(),
            status_eq: status
          },
          page,
          per_page: pageSize,
          sort_by: "updated_at_desc",
          search: false
        })
      )
    }
  }, [confirmedSearchQuery, page, pageSize, dispatch, selectedTrip, activeTab, key])

  // Update selectedTrip with full data from API response
  useEffect(() => {
    if (selectedTrip && searchTrips.length > 0 && !searchLoading) {
      // Find the matching trip in searchTrips and update selectedTrip with full data
      const fullTripData = searchTrips.find(trip => trip.id === selectedTrip.id)
      if (fullTripData && fullTripData !== selectedTrip) {
        setSelectedTrip(fullTripData)
      }
    }
  }, [searchTrips, searchLoading, selectedTrip])

  const handleSearchChange = useCallback((e) => {
    const newValue = e.target.value
    setSearchQuery(newValue)

    // Reset when search is completely cleared
    if (!newValue.trim()) {
      setSelectedTrip(null)
      setConfirmedSearchQuery("")
      setPage(1)
    } else if (selectedTrip && newValue.trim() !== selectedTrip.title) {
      // Clear selected trip when user edits the search to be different
      // This allows suggestions to appear again
      setSelectedTrip(null)
      setConfirmedSearchQuery("")
    }

    setShowSuggestions(true)
  }, [selectedTrip])

  // On Enter or click, fetch by id
  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && debouncedSearchQuery && debouncedSearchQuery.trim().length >= 3 && !searchLoading) {
        e.preventDefault()
        const allResults = [
          ...categorizedSearchResults.publishedTrips,
          ...categorizedSearchResults.drafts,
          ...categorizedSearchResults.deactivatedTrips
        ]

        // If there are results, confirm the search with the user's query (partial match)
        if (allResults.length > 0) {
          // Set confirmedSearchQuery - the useEffect will handle the API call
          setConfirmedSearchQuery(debouncedSearchQuery.trim())
          setShowSuggestions(false)
          setShowPagination(true)
          setSelectedTrip(null)
          setPage(1) // Reset to first page
        }
      }
    },
    [debouncedSearchQuery, searchLoading, categorizedSearchResults]
  )

  const handleTripClick = useCallback((trip) => {
    // Set selectedTrip immediately to prevent the search useEffect from triggering
    setSelectedTrip(trip)
    setSearchQuery(trip.title) // Update search query to selected trip's title
    setConfirmedSearchQuery(trip.title)
    setShowSuggestions(false)
    setShowPagination(false)
    setActiveTab(
      {
        [EXPERIENCE_STATES.published]: "1",
        [EXPERIENCE_STATES.draft]: "2",
        [EXPERIENCE_STATES.deactivated]: "3"
      }[trip.status]
    )

    // Then dispatch the API call
    dispatch(
      getGuideTripsSearch(null, {
        q: { status_eq: trip.status, title_eq: trip.title },
        page: 1,
        per_page: pageSize,
        sort_by: "updated_at_desc",
        search: false
      })
    )
  }, [dispatch, pageSize])

  const handleClearSearch = useCallback(() => {
    setSearchQuery("")
    setConfirmedSearchQuery("")
    setSelectedTrip(null)
    setShowSuggestions(false)
    setShowPagination(true)
    setPage(1)
    const tabStatusMap = { 1: "published", 2: "draft", 3: "unpublished" }
    const status = tabStatusMap[activeTab]
    dispatch(getGuideTrips(null, { q: { status_eq: status }, page: 1, per_page: pageSize, sort_by: "updated_at_desc" }))
  }, [dispatch, activeTab, pageSize])

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize)
    setPage(1) // Reset to first page when page size changes
  }, [])

  const handleTabChange = useCallback(
    (key) => {
      setActiveTab(key)
      setSelectedTrip(null)
      setSearchQuery("")
      setConfirmedSearchQuery("")
      setShowSuggestions(false)
      setShowPagination(true)
      setPage(1) // reset to first page
      navigate(routes.proExperiencesPath)
    },
    [navigate]
  )

  const renderSearchResults = () => {
    // Show suggestions only if user is actively typing (searchQuery exists) and suggestions are enabled
    if (!showSuggestions || !searchQuery.trim()) return null

    const hasResults = Object.values(categorizedSearchResults).some((list) => list.length > 0)
    const minCharacters = 3

    return (
      <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
        {searchQuery.trim().length < minCharacters ? (
          <div className={styles.noResults}>{t("global.type_at_least_characters", { count: minCharacters }) || `Type at least ${minCharacters} characters to search`}</div>
        ) : searchLoading ? (
          <div className={styles.noResults}>{t("loading")}</div>
        ) : (
          ["publishedTrips", "drafts", "deactivatedTrips"].map(
            (cat) =>
              categorizedSearchResults[cat].length > 0 && (
                <SearchCategory
                  key={cat}
                  title={t(`experiences.empty_state.${cat.replace("Trips", "")}`)}
                  trips={categorizedSearchResults[cat]}
                  onTripClick={handleTripClick}
                  selectedTrip={selectedTrip}
                />
              )
          )
        )}
        {!searchLoading && !hasResults && debouncedSearchQuery.trim().length >= minCharacters && <div className={styles.noResults}>{t("global.no_result_found")}</div>}
      </fieldset>
    )
  }

  const items = [
    {
      key: "1",
      label: `${t("experiences.empty_state.published")}`,
      children: (
        <TabContent
          onAddNewExperience={createTripHandler}
          type={EXPERIENCE_STATES.published}
          trips={mainListLogic.filteredTrips.publishedTrips}
          onActionComplete={() =>
            dispatch(getGuideTrips(null, { q: { status_eq: "published" }, page, per_page: pageSize, sort_by: "updated_at_desc" }))
          }
          metadata={metadata}
          page={page}
          showSuggestions={showSuggestions}
          showPagination={showPagination}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={handlePageSizeChange}
          loading={loading}
        />
      )
    },
    {
      key: "2",
      label: `${t("experiences.empty_state.drafts")}`,
      children: (
        <TabContent
          onAddNewExperience={createTripHandler}
          type={EXPERIENCE_STATES.draft}
          trips={mainListLogic.filteredTrips.drafts}
          onActionComplete={() =>
            dispatch(getGuideTrips(null, { q: { status_eq: "draft" }, page, per_page: pageSize, sort_by: "updated_at_desc" }))
          }
          metadata={metadata}
          page={page}
          pageSize={pageSize}
          showSuggestions={showSuggestions}
          showPagination={showPagination}
          setPage={setPage}
          setPageSize={handlePageSizeChange}
          loading={loading}
        />
      )
    },
    {
      key: "3",
      label: `${t("experiences.empty_state.deactivated")}`,
      children: (
        <TabContent
          onAddNewExperience={createTripHandler}
          type={EXPERIENCE_STATES.deactivated}
          trips={mainListLogic.filteredTrips.deactivatedTrips}
          onActionComplete={() =>
            dispatch(getGuideTrips(null, { q: { status_eq: "unpublished" }, page, per_page: pageSize, sort_by: "updated_at_desc" }))
          }
          metadata={metadata}
          page={page}
          showSuggestions={showSuggestions}
          showPagination={showPagination}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={handlePageSizeChange}
          loading={loading}
        />
      )
    }
  ]

  return {
    searchQuery,
    setSearchQuery,
    confirmedSearchQuery,
    handleSearchChange,
    handleSearchKeyDown,
    handleClearSearch,
    selectedTrip,
    createTripHandler,
    activeTab,
    handleTabChange,
    items,
    renderSearchResults,
    loading,
    metadata,
    originalStatusCount,
    showSuggestions,
    page,
    setPage,
    pageSize,
    setPageSize
  }
}
